/**
 * InlineNotesSection — S10-13
 *
 * Collapsible Notes-Section for Hotspot-Detail page.
 * Default state: collapsed if no notes, expanded if notes exist.
 * Reuses NotesList for the actual CRUD UI.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StickyNote, ChevronDown } from 'lucide-react'
import { useHotspotNotes } from './useHotspotNotes'
import { NotesList } from '../notes/NotesList'

type Props = {
  hotspotId: string | number
}

export function InlineNotesSection({ hotspotId }: Props) {
  const { t } = useTranslation('hotspots')
  const { data: notes } = useHotspotNotes(hotspotId)
  const hasNotes = Boolean(notes && notes.length > 0)

  // Default: collapsed if no notes, expanded if notes exist
  const [isOpen, setIsOpen] = useState(hasNotes)

  // Auto-expand, sobald Notizen geladen sind — adjust-during-render statt
  // setState im Effect (react-hooks/set-state-in-effect).
  const [prevHasNotes, setPrevHasNotes] = useState(hasNotes)
  if (hasNotes !== prevHasNotes) {
    setPrevHasNotes(hasNotes)
    if (hasNotes) setIsOpen(true)
  }

  const noteCount = notes?.length ?? 0

  return (
    <section
      aria-label={t('notes.sectionLabel')}
      style={{
        marginTop: 8,
        border: '1px solid var(--border, #e5e7eb)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
      }}
    >
      {/* Collapsible Header */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-controls="inline-notes-content"
        data-testid="inline-notes-toggle"
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'var(--surface)',
          border: 'none',
          padding: '16px',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 15,
          fontWeight: 600,
          color: 'var(--text)',
          gap: 12,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <StickyNote size={18} />
          {t('notes.heading')}
          {noteCount > 0 && (
            <span
              style={{
                fontSize: 12,
                fontWeight: 500,
                background: 'var(--primary-bg, rgba(59, 130, 246, 0.1))',
                color: 'var(--primary, #3b82f6)',
                padding: '2px 8px',
                borderRadius: 'var(--radius-full)',
              }}
            >
              {noteCount}
            </span>
          )}
        </span>
        <ChevronDown
          size={18}
          style={{
            transition: 'transform 0.2s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            flexShrink: 0,
          }}
        />
      </button>

      {/* Collapsible Content */}
      <div
        id="inline-notes-content"
        role="region"
        aria-labelledby="inline-notes-toggle"
        aria-hidden={!isOpen}
        data-testid="inline-notes-content"
        style={{
          overflow: 'hidden',
          maxHeight: isOpen ? '2000px' : '0',
          transition: 'max-height 0.3s ease',
        }}
      >
        <div style={{ padding: '0 16px 16px' }}>
          <NotesList originType="Hotspot" originId={Number(hotspotId)} />
        </div>
      </div>
    </section>
  )
}
